import React, { useState, useEffect, useRef } from 'react';
import { 
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  UserIcon,
  CalendarIcon,
  PaperClipIcon,
  DocumentIcon,
  PhotoIcon,
  XMarkIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import api from '../../services/api';
import axios from 'axios'; // TODO: rimuovere, usare api
import API_CONFIG from '../../config/api';

const TabComunicazioni = ({ pratica, currentUser, getTimeAgo }) => {
  const [messaggi, setMessaggi] = useState([]);
  const [nuovoMessaggio, setNuovoMessaggio] = useState('');
  const [loadingMessaggi, setLoadingMessaggi] = useState(true);
  const [loadingInvio, setLoadingInvio] = useState(false);
  const [allegatiSelezionati, setAllegatiSelezionati] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Carica i messaggi
  const caricaMessaggi = async () => {
    try {
      const response = await api.get(`/pratica/${pratica.id}/note`);
      if (response.data.success) {
        setMessaggi(response.data.note || []);
      }
    } catch (error) {
      console.error('Errore nel caricamento messaggi:', error);
      if (error.response?.status !== 404) {
        toast.error('Errore nel caricamento dei messaggi');
      }
    } finally {
      setLoadingMessaggi(false);
    }
  };

  // Aggiornamento automatico ogni 5 secondi
  useEffect(() => {
    caricaMessaggi();
    const interval = setInterval(caricaMessaggi, 5000);
    return () => clearInterval(interval);
  }, [pratica.id]);

  // Scroll automatico ai nuovi messaggi
  useEffect(() => {
    scrollToBottom();
  }, [messaggi]);

  // Gestione scroll per mostrare/nascondere pulsante "vai in fondo"
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollButton(!isNearBottom);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Gestione invio messaggio
  const inviaMessaggio = async (e) => {
    e.preventDefault();
    
    if (!nuovoMessaggio.trim() && allegatiSelezionati.length === 0) {
      toast.warning('Inserisci un messaggio o seleziona un allegato');
      return;
    }

    setLoadingInvio(true);

    try {
      const formData = new FormData();
      formData.append('testo', nuovoMessaggio);
      formData.append('idUtente', currentUser?.id || 1);
      
      // Passa il nome completo dell'utente
      const nomeCompleto = currentUser ? 
        (currentUser.nome && currentUser.cognome ? 
          `${currentUser.nome} ${currentUser.cognome}` : 
          currentUser.username || currentUser.email || `Utente ${currentUser.id}`
        ) : 'Utente';
      formData.append('autore', nomeCompleto);
      
      formData.append('tipo', 'comunicazione');

      // Aggiungi allegati
      allegatiSelezionati.forEach(file => {
        formData.append('allegati', file);
      });

      const response = await api.post(
        `/pratica/${pratica.id}/note`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setNuovoMessaggio('');
        setAllegatiSelezionati([]);
        await caricaMessaggi(); // Ricarica i messaggi
        toast.success('Messaggio inviato con successo');
      }
    } catch (error) {
      console.error('Errore nell\'invio del messaggio:', error);
      toast.error('Errore nell\'invio del messaggio');
    } finally {
      setLoadingInvio(false);
    }
  };

  // Gestione selezione file
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + allegatiSelezionati.length > 5) {
      toast.warning('Massimo 5 allegati per messaggio');
      return;
    }
    setAllegatiSelezionati(prev => [...prev, ...files]);
  };

  // Gestione drag & drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length + allegatiSelezionati.length > 5) {
      toast.warning('Massimo 5 allegati per messaggio');
      return;
    }
    setAllegatiSelezionati(prev => [...prev, ...files]);
  };

  // Rimuovi allegato
  const rimuoviAllegato = (index) => {
    setAllegatiSelezionati(prev => prev.filter((_, i) => i !== index));
  };

  // Formatta timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) { // 7 giorni
      return date.toLocaleDateString('it-IT', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' });
    }
  };

  // Ottieni colore avatar
  const getAvatarColor = (userId) => {
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500', 
      'bg-indigo-500', 'bg-yellow-500', 'bg-red-500', 'bg-teal-500'
    ];
    return colors[userId % colors.length];
  };

  // Ottieni icona per tipo file
  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
      return <PhotoIcon className="h-4 w-4" />;
    }
    return <DocumentIcon className="h-4 w-4" />;
  };

  // Download allegato
  const downloadAllegato = async (messaggioId, allegatoIndex, nomeFile) => {
    try {
      // Costruisci l'ID allegato come "messaggioId-allegatoIndex"
      const allegatoId = `${messaggioId}-${allegatoIndex}`;
      
      const response = await api.get(
        `/pratica/${pratica.id}/note/${allegatoId}/download`,
        { responseType: 'blob' }
      );
      
      // Crea URL per il download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nomeFile);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Allegato scaricato con successo');
    } catch (error) {
      console.error('Errore durante il download dell\'allegato:', error);
      toast.error('Errore durante il download dell\'allegato');
    }
  };

  if (loadingMessaggi) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Caricamento messaggi...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header Chat */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 shadow-lg">
        <div className="flex items-center space-x-3">
          <ChatBubbleLeftRightIcon className="h-6 w-6" />
          <div>
            <h3 className="font-semibold">Comunicazioni Pratica</h3>
            <p className="text-blue-100 text-sm">
              {pratica.codicePratica} • {messaggi.length} messaggi
            </p>
          </div>
        </div>
      </div>

      {/* Area Messaggi */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 relative"
        style={{ maxHeight: 'calc(100vh - 300px)' }}
      >
        {messaggi.length === 0 ? (
          <div className="text-center py-12">
            <ChatBubbleLeftRightIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Nessun messaggio ancora</p>
            <p className="text-gray-400 text-sm">Inizia una conversazione!</p>
          </div>
        ) : (
          messaggi.map((messaggio) => {
            const isCurrentUser = messaggio.idUtente === currentUser?.id;
            
            return (
              <div
                key={messaggio.id}
                className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex items-start space-x-2 max-w-xs lg:max-w-md ${isCurrentUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  {/* Avatar */}
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${getAvatarColor(messaggio.idUtente || 0)}`}>
                    {messaggio.nomeUtente ? messaggio.nomeUtente.charAt(0).toUpperCase() : 'S'}
                  </div>
                  
                  {/* Messaggio */}
                  <div className={`rounded-lg p-3 shadow-sm ${
                    isCurrentUser 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-white text-gray-800 border border-gray-200'
                  }`}>
                    {/* Nome utente e timestamp */}
                    <div className={`text-xs mb-1 ${isCurrentUser ? 'text-blue-100' : 'text-gray-500'}`}>
                      <span className="font-medium">{messaggio.nomeUtente}</span>
                      <span className="ml-2">{formatTimestamp(messaggio.dataCreazione)}</span>
                    </div>
                    
                    {/* Testo messaggio */}
                    {messaggio.testo && (
                      <p className="text-sm whitespace-pre-wrap break-words">
                        {messaggio.testo}
                      </p>
                    )}
                    
                    {/* Allegati */}
                    {messaggio.allegati && messaggio.allegati.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {messaggio.allegati.map((allegato, index) => (
                          <div
                            key={index}
                            className={`flex items-center space-x-2 p-2 rounded ${
                              isCurrentUser ? 'bg-blue-500' : 'bg-gray-100'
                            }`}
                          >
                            {getFileIcon(allegato.nome)}
                            <span className="text-xs truncate flex-1">{allegato.nome}</span>
                            <button
                              onClick={() => downloadAllegato(messaggio.id, index, allegato.nome)}
                              className={`text-xs underline ${
                                isCurrentUser ? 'text-blue-100 hover:text-white' : 'text-blue-600 hover:text-blue-800'
                              }`}
                            >
                              Scarica
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        
        <div ref={messagesEndRef} />
        
        {/* Pulsante scroll to bottom */}
        {showScrollButton && (
          <button
            onClick={scrollToBottom}
            className="fixed bottom-32 right-8 bg-blue-600 text-white p-2 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-10"
          >
            <ArrowDownIcon className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Area Input */}
      <div className="bg-white border-t border-gray-200 p-4">
        {/* Anteprima allegati */}
        {allegatiSelezionati.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {allegatiSelezionati.map((file, index) => (
              <div key={index} className="flex items-center space-x-2 bg-gray-100 rounded-lg p-2">
                {getFileIcon(file.name)}
                <span className="text-sm text-gray-700 truncate max-w-32">{file.name}</span>
                <button
                  onClick={() => rimuoviAllegato(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Form invio messaggio */}
        <form onSubmit={inviaMessaggio} className="flex items-end space-x-2">
          <div className="flex-1">
            <div
              className={`border-2 border-dashed rounded-lg p-2 transition-colors ${
                isDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <textarea
                value={nuovoMessaggio}
                onChange={(e) => setNuovoMessaggio(e.target.value)}
                placeholder="Scrivi un messaggio... (trascina file qui per allegarli)"
                className="w-full resize-none border-0 focus:ring-0 focus:outline-none bg-transparent"
                rows="2"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    inviaMessaggio(e);
                  }
                }}
              />
            </div>
          </div>
          
          {/* Pulsante allegati */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
            title="Aggiungi allegati"
          >
            <PaperClipIcon className="h-5 w-5" />
          </button>
          
          {/* Pulsante invio */}
          <button
            type="submit"
            disabled={loadingInvio || (!nuovoMessaggio.trim() && allegatiSelezionati.length === 0)}
            className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loadingInvio ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <PaperAirplaneIcon className="h-5 w-5" />
            )}
          </button>
        </form>

        {/* Input file nascosto */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          accept="*/*"
        />
      </div>
    </div>
  );
};

export default TabComunicazioni; 